import uuid from 'uuid/v4';
import isEqual from 'lodash/isEqual';
import { dispatchEvent } from '@/overlord/events';
import { isArray } from 'lodash';
import { difference } from '@/utils';
import api from '../api';

export const PRIVATE_CLIPBOARD = 'private';
export const PUBLIC_CLIPBOARD = 'public';
export const PUBLIC_CLIPBOARD_ITEMS_UPDATED_EVENT = 'PUBLIC_CLIPBOARD_ITEMS_UPDATED_EVENT';

const insertAt = (arr, index, newItem) => [
  ...arr.slice(0, index),
  newItem,
  ...arr.slice(index),
];

export default {
  namespaced: true,
  state: {
    current: null,
    private: {
      id: null,
      items: [],
    },
    public: {
      id: null,
      items: [],
    },
    modulesConfig: {},
    currentSiteAlias: null,
  },
  getters: {
    active: state => state.current !== null,
    current: state => state.current,
    id: state => state[state.current].id,
    privateClipboardId: state => state.private.id,
    publicClipboardId: (state, getters, rootState) => rootState.config.clipboard.id,
    items: state => (state[state.current] ? state[state.current].items : []),
    moduleConfig: state => (moduleType) => {
      if (!state.modulesConfig[moduleType]) {
        return null;
      }
      return state.modulesConfig[moduleType];
    },
  },
  actions: {
    async LOAD_PUBLIC({ rootGetters, commit, dispatch }, id) {
      await dispatch('FETCH_PUBLIC', id);
      commit('SET_CURRENT_SITE_ALIAS', rootGetters.site.idAlias);
      commit('SET_CURRENT_CLIPBOARD', PUBLIC_CLIPBOARD);
    },
    async FETCH_PUBLIC({ commit }, id) {
      // Fetch initial items
      const items = await api.fetchClipboard(id);
      if (!items) {
        return;
      }
      commit('SET_PUBLIC_CLIPBOARD_ID', id);
      commit('SET_PUBLIC_CLIPBOARD_ITEMS', items);
    },
    async RELOAD_PUBLIC({ state, commit }) {
      const items = await api.fetchClipboard(state.public.id);
      if (!items) {
        return;
      }
      commit('SET_PUBLIC_CLIPBOARD_ITEMS', items);
    },
    async LOAD_PRIVATE({ rootGetters, commit }, id) {
      // Fetch initial items
      const items = await api.fetchClipboard(id);
      if (!items) {
        return;
      }
      commit('SET_CURRENT_SITE_ALIAS', rootGetters.site.idAlias);
      commit('SET_CURRENT_CLIPBOARD', PRIVATE_CLIPBOARD);
      commit('SET_PRIVATE_CLIPBOARD_ID', id);
      commit('SET_PRIVATE_CLIPBOARD_ITEMS', { items, idAlias: rootGetters.site.idAlias });
    },
    async RELOAD_PRIVATE({ rootGetters, state, commit }) {
      const items = await api.fetchClipboard(state.private.id);
      if (!items) {
        return;
      }
      const diff = difference(items, state.private.items);
      // Avoid infinite loop when event triggered this action from diferent site tabs
      if (isArray(diff) && diff.length > 0) {
        commit('SET_PRIVATE_CLIPBOARD_ITEMS', { items, idAlias: rootGetters.site.idAlias });
      }
    },
    async ADD_MODULES_CONFIG({ commit }, moduleConfig) {
      commit('ADD_MODULES_CONFIG', moduleConfig);
    },
    async DESTROY({ rootGetters, commit, dispatch }) {
      commit('SET_CURRENT_CLIPBOARD', null);
      commit('SET_PRIVATE_CLIPBOARD_ID', null);
      commit('SET_PRIVATE_CLIPBOARD_ITEMS', { items: [], idAlias: rootGetters.site.idAlias });
      dispatch('bag/RESET_TABS', null, { root: true });
    },
    async ADD({ state, dispatch }, item) {
      return state.current === PUBLIC_CLIPBOARD
        ? dispatch('ADD_PUBLIC', { item })
        : dispatch('ADD_PRIVATE', { item });
    },
    async CLONE({ state, dispatch }, item) {
      const clone = {
        ...item,
        uuid: uuid(),
      };

      // Delete multisite properties when clone item
      delete clone.sourceSiteAlias
      delete clone.multisiteSent

      // We insert cloned item at first of list
      return state.current === PUBLIC_CLIPBOARD
        ? dispatch('ADD_PUBLIC', { item: clone })
        : dispatch('ADD_PRIVATE', { item: clone });
    },
    async SET_PUBLIC({ state, commit }, items) {
      if (isEqual(items, state[PUBLIC_CLIPBOARD].items)) return;
      commit('SET_PUBLIC_CLIPBOARD_ITEMS', items);
    },
    async ADD_PUBLIC({ state, commit, rootGetters }, { item, index = 0 }) {
      const items = insertAt(state[PUBLIC_CLIPBOARD].items, index, item);
      await api.saveClipboard(state[PUBLIC_CLIPBOARD].id, items);
      commit('SET_PUBLIC_CLIPBOARD_ITEMS', items);
      dispatchEvent(PUBLIC_CLIPBOARD_ITEMS_UPDATED_EVENT, {
        items: state[PUBLIC_CLIPBOARD].items,
        idAlias: rootGetters.site.idAlias,
      });
    },
    async ADD_PRIVATE({ state, commit, rootGetters }, { item, index = 0 }) {
      const items = insertAt(state[PRIVATE_CLIPBOARD].items, index, item);
      commit('SET_PRIVATE_CLIPBOARD_ITEMS', { items, idAlias: rootGetters.site.idAlias });

      // If list items is bigger than api limit, api remove last item. We update list items with api response
      const { data } = await api.saveClipboard(state[PRIVATE_CLIPBOARD].id, state[PRIVATE_CLIPBOARD].items);
      commit('SET_PRIVATE_CLIPBOARD_ITEMS', { items: data, idAlias: rootGetters.site.idAlias });
    },
    async REMOVE({ state, dispatch }, item) {
      return state.current === PUBLIC_CLIPBOARD
        ? dispatch('REMOVE_PUBLIC', item)
        : dispatch('REMOVE_PRIVATE', item);
    },
    async REMOVE_PUBLIC({ state, commit, rootGetters }, item) {
      const items = state[PUBLIC_CLIPBOARD].items.filter(i => item.uuid !== i.uuid);
      await api.saveClipboard(state[PUBLIC_CLIPBOARD].id, items);
      commit('SET_PUBLIC_CLIPBOARD_ITEMS', items);
      dispatchEvent(PUBLIC_CLIPBOARD_ITEMS_UPDATED_EVENT, {
        items: state[PUBLIC_CLIPBOARD].items,
        idAlias: rootGetters.site.idAlias,
      });
    },
    async REMOVE_PRIVATE({ rootGetters, state, commit }, item) {
      const items = state[PRIVATE_CLIPBOARD].items.filter(i => item.uuid !== i.uuid);
      commit('SET_PRIVATE_CLIPBOARD_ITEMS', { items, idAlias: rootGetters.site.idAlias });
      api.saveClipboard(state[PRIVATE_CLIPBOARD].id, state[PRIVATE_CLIPBOARD].items);
    },
    async RENAME({ state, dispatch }, { item, title }) {
      return state.current === PUBLIC_CLIPBOARD
        ? dispatch('RENAME_PUBLIC', { item, title })
        : dispatch('RENAME_PRIVATE', { item, title });
    },
    async RENAME_PUBLIC({ state, commit, rootGetters }, { item, title }) {
      const items = state[PUBLIC_CLIPBOARD].items.map((i) => {
        if (item.uuid === i.uuid) {
          i.clipboardTitle = title;
        }
        return i;
      });
      await api.saveClipboard(state[PUBLIC_CLIPBOARD].id, items);
      commit('SET_PUBLIC_CLIPBOARD_ITEMS', items);
      dispatchEvent(PUBLIC_CLIPBOARD_ITEMS_UPDATED_EVENT, {
        items: state[PUBLIC_CLIPBOARD].items,
        idAlias: rootGetters.site.idAlias,
      });
    },
    async RENAME_PRIVATE({ rootGetters, state, commit }, { item, title }) {
      const items = state[PRIVATE_CLIPBOARD].items.map((i) => {
        if (item.uuid === i.uuid) {
          i.clipboardTitle = title;
        }
        return i;
      });
      commit('SET_PRIVATE_CLIPBOARD_ITEMS', { items, idAlias: rootGetters.site.idAlias });
      api.saveClipboard(state[PRIVATE_CLIPBOARD].id, state[PRIVATE_CLIPBOARD].items);
    },
    MOVE_ITEM_TO_PUBLIC({ state, dispatch }, item) {
      dispatch('ADD_PUBLIC', { item });
      dispatch('REMOVE_PRIVATE', { item });
    },
    CONVERT_ITEM_TO_MULTISITE({ state, dispatch }, { item, idAlias }) {
      return state.current === PUBLIC_CLIPBOARD
        ? dispatch('CONVERT_ITEM_TO_MULTISITE_PUBLIC', { item, idAlias })
        : dispatch('CONVERT_ITEM_TO_MULTISITE_PRIVATE', { item, idAlias });
    },
    async CONVERT_ITEM_TO_MULTISITE_PUBLIC({ state, commit, rootGetters }, { item, idAlias }) {
      const items = state[PUBLIC_CLIPBOARD].items.map((i) => {
        if (item.uuid === i.uuid) {
          i.sourceSiteAlias = idAlias;
        }
        return i;
      });
      await api.saveClipboard(state[PUBLIC_CLIPBOARD].id, items);
      commit('SET_PUBLIC_CLIPBOARD_ITEMS', items);
      dispatchEvent(PUBLIC_CLIPBOARD_ITEMS_UPDATED_EVENT, {
        items: state[PUBLIC_CLIPBOARD].items,
        idAlias: rootGetters.site.idAlias,
      });
    },
    async CONVERT_ITEM_TO_MULTISITE_PRIVATE({ rootGetters, state, commit }, { item, idAlias }) {
      const items = state[PRIVATE_CLIPBOARD].items.map((i) => {
        if (item.uuid === i.uuid) {
          i.sourceSiteAlias = idAlias;
        }
        return i;
      });
      await api.saveClipboard(state[PRIVATE_CLIPBOARD].id, items);
      commit('SET_PRIVATE_CLIPBOARD_ITEMS', { items, idAlias: rootGetters.site.idAlias });
    },
  },
  mutations: {
    async ADD_MODULES_CONFIG(state, modulesConfig) {
      state.modulesConfig = { ...state.modulesConfig, ...modulesConfig };
    },
    SET_PUBLIC_CLIPBOARD_ID: (state, id) => {
      state[PUBLIC_CLIPBOARD].id = id;
    },
    SET_PUBLIC_CLIPBOARD_ITEMS: (state, items) => {
      state[PUBLIC_CLIPBOARD].items = items || [];
    },
    SET_PRIVATE_CLIPBOARD_ID: (state, id) => {
      state[PRIVATE_CLIPBOARD].id = id;
    },
    SET_PRIVATE_CLIPBOARD_ITEMS: (state, { items, idAlias }) => {
      if (idAlias === state.currentSiteAlias) {
        state[PRIVATE_CLIPBOARD].items = items || [];
      }
    },
    SET_CURRENT_SITE_ALIAS: (state, idAlias) => {
      state.currentSiteAlias = idAlias;
    },
    SET_CURRENT_CLIPBOARD: (state, type) => {
      state.current = type;
    },
  },
};
